import React, { useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import { Autocomplete, Box, Divider, ClickAwayListener, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { createJiraSubTaskForAdditionalInfo, getJiraUser} from '../../services/jira.service';

import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useState } from "react";
import _ from 'lodash';
import InfoIcon from '@mui/icons-material/Info';
import "./taskComponents.scss"


const additionalInfoTooltipMessage = (
  <div>
    <span>Here you create a sub-task or feedback ticket for gathering additional details related to the selected task for respective the person.</span>
    <br></br>
    <span>Note: Creating Ticket will block the respective parent ticket</span>
  </div>
);

interface DialogProps {
  isOpen: boolean;
  onClose: ( isRefetch?:boolean) => void;
  onCreateSubTask: () => void;
  feedBackOptions: any;
}

const AdditionalInfoTaskDialog: React.FC<DialogProps> = ({ isOpen, onClose, feedBackOptions, onCreateSubTask }) => {

  const issueDetails = useSelector((state: RootState) => state.issue);

  const [validationObject, setValidationObject] = useState<any>({});
  const [searchUserList, setSearchUserList] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccountId , setSelectedAccountId ] = useState("");
  const [isInfoToolTipOpen, setIsInfoToolTipOpen] =useState(false);
  const userState = useSelector((state: RootState) => state.user);
  const userDetails = userState?.user;

  const handleClose = ()=>{
    onClose();
    setSearchUserList([]);
    formik.resetForm();
  }

  let initialValues: any = {};

  useEffect(() => {
    getJiraUserList({size: 20});

    if(feedBackOptions[0] && feedBackOptions[0].userInputs && feedBackOptions[0].userInputs.length) {
      let validations: any = {};
      initialValues.person = '';
      validations.person = Yup.string().required();
      feedBackOptions[0].userInputs.forEach((fields: any) => {
        initialValues[fields.variableName] = fields.defaultValue;
        validations[fields.variableName] = Yup.string().required(`${fields.displayName} is required`);
        setValidationObject(validations);
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedBackOptions])

  const onSubmit = async (values: typeof initialValues ) => {
      try {
        if(!selectedAccountId || values.person === '') {
          formik.setErrors({
            ...formik.errors,
            person: 'Person is required'
          })
          return;
        }

        let reqBody = {
          userId: userDetails?.jiraDetails?.accountId,
          assignee: selectedAccountId,
          issueId: issueDetails.id,
          actionId: feedBackOptions[0].id,
          userInputs: {} as any
        };
        feedBackOptions[0].userInputs.forEach((fields: any) => {
          reqBody.userInputs[fields.variableName] = {
            inputType: fields.inputType,
            value: values[fields.variableName]
          };
        });

        let response = await createJiraSubTaskForAdditionalInfo(reqBody);
        if(response && response.statusCode && response.statusCode === 200)  {
          toast.success('Additional info task created successfully')
        } else {
          toast.error('Additional info task creation failed')
        }
        // Reset form fields
        formik.resetForm();
        onClose();
      } catch (error) {
        toast.error('Additional info task creation failed')
      }

  };


  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape(validationObject),
    onSubmit
  });

  const getJiraUserList = async(params: any) => {
    setLoading(true);
    let userData :any = [];
    try {
      const userDataResponse = await getJiraUser(params);
      userData = userDataResponse?.data;
    } catch (error) {
      userData = [];
    } finally {
      setLoading(false);
    }
    setSearchUserList([...userData]);
    return userData;
  };

  const handleCreateSubTask = () => {
    handleClose();
    onCreateSubTask();
  }

  const debouncedSearch = useCallback(
    _.debounce(async () => {
      getJiraUserList({size: 20, text: formik.values.person});
    }, 2000),
    [getJiraUserList]
  );

  useEffect(() => {
    setLoading(true);
    setSearchUserList([]);
    debouncedSearch();
    return () => {
      debouncedSearch.cancel();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.person])

  return (
    <Dialog className='additional-info-dialog-container' open={isOpen}>
      <DialogTitle >
      <div className='title-container'>
            <span>Need Additional Information</span>

            <ClickAwayListener onClickAway={(e)=>setIsInfoToolTipOpen(false)}>
              <div>
                <Tooltip
                  title={additionalInfoTooltipMessage}
                  PopperProps={{
                    disablePortal: true,
                  }}
                  onClose={(e)=>setIsInfoToolTipOpen(false)}
                  open={isInfoToolTipOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                >
                  <Button className='info-button'  onClick={(e)=>setIsInfoToolTipOpen(true)}><InfoIcon/></Button>
                </Tooltip>
              </div>
            </ClickAwayListener>
          </div>
      </DialogTitle>
      <Button variant="outlined" color="info" sx={{margin: '20px', marginBottom: '30px', marginTop: '0px'}} onClick={handleCreateSubTask}>
        Create Ticket
      </Button>
      <Divider sx={{height: 2, width: '100%', alignItems: 'center'}}>or</Divider>
      <DialogContent sx={{maxHeight:'400px', alignContent: 'center'}}>
        <form onSubmit={formik.handleSubmit} style={{ marginTop: '10px'}}>

          <div>
              {!!issueDetails?.id && (
                <TextField
                  label="Selected Task"
                  value={`${issueDetails?.key || ''} ${issueDetails?.issue || ''}`}
                  disabled
                  fullWidth
                  margin="normal"
                />
              )
            }

            <Autocomplete
              id="country-select-demo"
              options={searchUserList}    
              loading={loading}        
              autoHighlight
              onChange={(event, value: any) => {
                  const selectedAccountId = value?.accountId || '';
                  setSelectedAccountId(selectedAccountId);   
                  formik.setValues({
                    ...formik.values,
                    person: value?.displayName || ''
                  })               
              }}
              isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
              getOptionLabel={(option: any) => option.displayName}
              renderOption={(props, option) => (
                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props} key={option.accountId}>
                  <img
                    loading="lazy"
                    width="20"
                    src={option.avatarUrls['16x16']}
                    alt={option.displayName}
                  />
                  {option.displayName}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Person"
                  name="person"
                  value={formik.values.person || ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password',
                  }}
                  error={formik.touched.person && Boolean(formik.errors.person)}
                  helperText={formik.touched.person && formik.errors.person && `Person is required`}
                />
              )}
            />
            { (feedBackOptions[0] && feedBackOptions[0].userInputs && feedBackOptions[0].userInputs) &&
              feedBackOptions[0].userInputs.map((field: any) => {
                return (
                  <TextField
                    rows={3}
                    label={field.displayName}
                    type="text"
                    name={field.variableName}
                    value={formik.values[field.variableName]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched[field.variableName] && Boolean(formik.errors[field.variableName])}
                    helperText={formik.touched[field.variableName] && formik.errors[field.variableName] && `${field.displayName} is required`}
                    fullWidth
                    margin="normal"
                  />
                )
              })
            }
          </div>
          <div className='action-button' style={{ display: 'flex' , justifyContent: 'space-between'}} >
            <Button variant="contained" color="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Submit
            </Button>
          </div>

        </form>
      </DialogContent>
    </Dialog>
  );
};


export default AdditionalInfoTaskDialog;
